import React, { useEffect, useRef, useState } from "react";
import { Input, Pagination, Spin, message } from "antd";
import "./TextSelector.css";
import { get, post, put, remove } from "../../../utils/apiUtils";
import {
  RightOutlined,
  DownOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { getTextPositions } from "../../../utils/getTextPositions";

interface TextSelectorProps {
  htmlContent: string;
  onBack: () => void;
  projectId: string | undefined;
  documentId: string | null;
  onHighlightChanged?: () => void;
}
interface HighlightedData {
  text: string;
  code: { id: string; name: string };
  fileName: string;
  codes?: { id: string; name: string }[];
  highlightId?: string;
}

const BASE_CSS_GENERIC = `
html,body{margin:0;background:#fff}
*{box-sizing:border-box}
img,svg,video{max-width:100%;height:auto}
p{margin:0}
body{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
`;

function normalizeForSearch(s: string) {
  return s
    .replace(/\u00AD/g, "")
    .replace(/-\s*\n\s*/g, "")
    .replace(/\s*\n+\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Kthen tekstin FIKS të range-it (pa i normalizu spacat).
 */
function getExactRangeText(r: Range): string {
  const doc = r.commonAncestorContainer.ownerDocument!;
  const startNode = r.startContainer;
  const endNode = r.endContainer;

  const quick = r.cloneContents().textContent || "";
  if (quick) return quick;

  const walker = doc.createTreeWalker(
    r.commonAncestorContainer,
    NodeFilter.SHOW_TEXT
  );
  const out: string[] = [];
  let inRange = false;
  let n: Node | null = walker.currentNode;

  while ((n = walker.nextNode())) {
    const txt = n.textContent || "";
    if (!inRange && n === startNode) {
      inRange = true;
      if (n === endNode) {
        out.push(txt.slice(r.startOffset, r.endOffset));
        break;
      } else {
        out.push(txt.slice((r.startOffset as number) || 0));
      }
    } else if (inRange) {
      if (n === endNode) {
        out.push(txt.slice(0, (r.endOffset as number) || 0));
        break;
      } else {
        out.push(txt);
      }
    }
  }
  return out.join("");
}

function extendRightIfWord(hay: string, start: number, end: number) {
  const isWord = (ch: string) => /[A-Za-zÀ-ÿ0-9_]/.test(ch);
  let e = end;
  while (e < hay.length && isWord(hay[e])) e++;
  return { start, end: e };
}

const TextSelector: React.FC<TextSelectorProps> = ({
  htmlContent,
  onBack,
  projectId,
  documentId,
  onHighlightChanged,
}) => {
  const documentIdRef = useRef<string | null>(documentId);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [frameDoc, setFrameDoc] = useState<Document | null>(null);
  const [frameWin, setFrameWin] = useState<Window | null>(null);
  const [isPdfDoc, setIsPdfDoc] = useState(false);

  const [selectedText, setSelectedText] = useState("");
  const [popupPos, setPopupPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [codes, setCodes] = useState<any[]>([]);
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [selectionRange, setSelectionRange] = useState<Range | null>(null);
  const [selectedHighlights, setSelectedHighlights] = useState<
    HighlightedData[]
  >([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [codePage, setCodePage] = useState(1);
  const [codeTotal, setCodeTotal] = useState(0);
  const [isAssigning, setIsAssigning] = useState(false);

  const [isFetchingCodes, setIsFetchingCodes] = useState(false);
  const [isFetchingHighlight, setIsFetchingHighlight] = useState(false);
  const [inlineBusy, setInlineBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingHighlightId, setUpdatingHighlightId] = useState<string | null>(
    null
  );

  // id → hash_color
  const [codeColorMap, setCodeColorMap] = useState<Record<string, string>>({});

  const lastCreatedHighlightElRef = useRef<HTMLElement | null>(null);
  const activeHighlightIdRef = useRef<string | null>(null);
  const editingHighlightIdRef = useRef<string | null>(null);
  const editingHighlightTextRef = useRef<string | null>(null);
  const [editingHighlightId, setEditingHighlightId] = useState<string | null>(
    null
  );
  const [codeSearch, setCodeSearch] = useState("");

  const overlayGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (documentId) documentIdRef.current = documentId;
  }, [documentId]);

  // teksti i dukshëm nga iframe
  const getVisibleHay = () => frameDoc?.body?.textContent ?? "";

  // ----- Render në iframe
  useEffect(() => {
    if (!iframeRef.current) return;
    const iframe = iframeRef.current;
    const isFullDoc =
      /^\s*<!doctype/i.test(htmlContent) || /^\s*<html/i.test(htmlContent);

    const overlayCss = `
      .hs-overlay{
        position:absolute;
        background: rgba(160,114,235,.35);
        border-radius: 2px;
        box-shadow: 0 0 0 1px rgba(160,114,235,.45) inset;
        cursor: pointer;
        pointer-events:auto;
      }
      .hs-overlay:hover{ box-shadow: 0 0 0 2px rgba(0,0,0,.08) inset; }
      .highlighted-code{
        background: rgba(160,114,235,.35);
        border-radius: 2px; padding: 0 2px; cursor: pointer;
      }
      .pdf-page{ position:relative; }
      .textLayer{ position:absolute; inset:0; z-index:2; }
      .textLayer .hs-text-overlay{ position:absolute; inset:0; pointer-events:none; z-index:3; }
    `;

    const doc = iframe.contentDocument!;
    doc.open();
    if (isFullDoc) {
      doc.write(htmlContent);
      const style = doc.createElement("style");
      style.textContent = overlayCss;
      doc.head.appendChild(style);
    } else {
      doc.write(
        `<!doctype html><html><head><meta charset="utf-8">
         <style>${BASE_CSS_GENERIC}${overlayCss}</style></head>
         <body><div id="page-wrapper">${htmlContent}</div></body></html>`
      );
    }
    doc.close();
    // e bëjmë body relative që overlay-t absolute të pozicionohen mirë
    if (doc.body && getComputedStyle(doc.body).position === "static") {
      doc.body.style.position = "relative";
    }

    setFrameDoc(doc);
    setFrameWin(iframe.contentWindow!);
    setIsPdfDoc(!!doc.querySelector(".textLayer"));
  }, [htmlContent]);

  function ensureTextLayerOverlay(textLayerEl: HTMLElement): HTMLElement {
    let ov = textLayerEl.querySelector<HTMLElement>(".hs-text-overlay");
    if (!ov) {
      ov = textLayerEl.ownerDocument!.createElement("div");
      ov.className = "hs-text-overlay";
      (ov.style as any).pointerEvents = "none";
      textLayerEl.appendChild(ov);
    }
    return ov;
  }

  function clearTempOverlays() {
    if (!frameDoc) return;
    frameDoc.querySelectorAll(".hs-overlay.temp").forEach((n) => n.remove());
  }

  function getPdfSelectionRectsByTextLayer(
    doc: Document,
    range: Range
  ): Array<{
    rect: DOMRect;
    textLayer: HTMLElement;
    left: number;
    top: number;
  }> {
    const src = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0.5 && r.height > 0.5
    );
    const out: Array<{
      rect: DOMRect;
      textLayer: HTMLElement;
      left: number;
      top: number;
    }> = [];
    for (const r of src) {
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const el = doc.elementFromPoint(cx, cy) as HTMLElement | null;
      const tl = el?.closest(".textLayer") as HTMLElement | null;
      if (!tl) continue;
      const tr = tl.getBoundingClientRect();
      out.push({
        rect: r,
        textLayer: tl,
        left: r.left - tr.left,
        top: r.top - tr.top,
      });
    }
    return out;
  }

  // ----- Listeners
  useEffect(() => {
    if (!frameDoc || !frameWin || !iframeRef.current) return;
    const iframe = iframeRef.current;

    const handleContextMenu = (e: MouseEvent) => {
      const sel = frameWin.getSelection();
      const has = sel?.rangeCount ? sel.getRangeAt(0) : null;
      if (has) {
        e.preventDefault();
        const exact = getExactRangeText(has);
        setSelectedText(exact);
        setSelectionRange(has.cloneRange());

        const rect = iframe.getBoundingClientRect();
        setPopupPos({ x: rect.left + e.clientX, y: rect.top + e.clientY });

        const trg = (e.target as HTMLElement).closest(
          ".hs-overlay, .highlighted-code"
        ) as HTMLElement | null;
        activeHighlightIdRef.current = trg?.dataset.highlightId ?? null;
      } else {
        setPopupPos(null);
        activeHighlightIdRef.current = null;
      }
    };

    const handleClick = (e: MouseEvent) => {
      const trg = (e.target as HTMLElement).closest(
        ".hs-overlay, .highlighted-code"
      ) as HTMLElement | null;
      if (
        trg?.dataset?.highlightId &&
        trg.dataset.highlightId !== "__pending__"
      ) {
        fetchHighlightById(trg.dataset.highlightId);
      }
    };

    frameDoc.addEventListener("contextmenu", handleContextMenu);
    frameDoc.addEventListener("click", handleClick);
    return () => {
      frameDoc.removeEventListener("contextmenu", handleContextMenu);
      frameDoc.removeEventListener("click", handleClick);
    };
  }, [frameDoc, frameWin, isPdfDoc, projectId]);

  // ----- TEMP highlight: TANI VETËM OVERLAY, pa span-e në HTML
  const showTemporaryHighlight = () => {
    if (!selectionRange || selectionRange.collapsed || !frameDoc || !frameWin)
      return;

    clearTempOverlays();
    overlayGroupRef.current = `grp_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;

    if (isPdfDoc) {
      const items = getPdfSelectionRectsByTextLayer(frameDoc, selectionRange);
      items.forEach(({ rect, textLayer, left, top }) => {
        const container = ensureTextLayerOverlay(textLayer);
        const box = frameDoc.createElement("div");
        box.className = "hs-overlay temp";
        box.style.left = `${left}px`;
        box.style.top = `${top}px`;
        box.style.width = `${rect.width}px`;
        box.style.height = `${rect.height}px`;
        box.dataset.group = overlayGroupRef.current!;
        (box.style as any).pointerEvents = "auto";
        container.appendChild(box);
      });
    } else {
      const rects = Array.from(selectionRange.getClientRects()).filter(
        (r) => r.width > 0.5 && r.height > 0.5
      );
      const bodyRect = frameDoc.body.getBoundingClientRect();
      rects.forEach((r) => {
        const box = frameDoc.createElement("div");
        box.className = "hs-overlay temp";
        box.style.left = `${r.left - bodyRect.left}px`;
        box.style.top = `${r.top - bodyRect.top}px`;
        box.style.width = `${r.width}px`;
        box.style.height = `${r.height}px`;
        box.dataset.group = overlayGroupRef.current!;
        (box.style as any).pointerEvents = "auto";
        frameDoc.body.appendChild(box);
      });
    }

    frameWin.getSelection()?.removeAllRanges();
  };

  /**
   * highlightSelection: TANI PËR TË DYJA (PDF + HTML) VETËM ME .hs-overlay
   */
  const highlightSelection = (color: string, codeNames: string[]) => {
    if (!frameDoc) return null;
    if (!overlayGroupRef.current) return null;

    const boxes = Array.from(
      frameDoc.querySelectorAll(
        `.hs-overlay.temp[data-group="${overlayGroupRef.current}"]`
      )
    ) as HTMLElement[];

    if (!boxes.length) return null;

    boxes.forEach((b) => {
      b.classList.remove("temp");
      b.style.background = color ? `${color}66` : "rgba(160,114,235,.35)";
      b.title = codeNames.join(", ");
      b.dataset.highlightId = "__pending__";
      b.removeAttribute("data-group");
      (b.style as any).pointerEvents = "auto";
    });
    lastCreatedHighlightElRef.current = boxes[0];
    return boxes[0];
  };

  const refreshHighlightTooltip = (
    highlightId: string,
    codeNames: string[]
  ) => {
    if (!frameDoc) return;
    frameDoc
      .querySelectorAll(
        `.hs-overlay[data-highlight-id="${highlightId}"], .highlighted-code[data-highlight-id="${highlightId}"]`
      )
      .forEach((el) => ((el as HTMLElement).title = codeNames.join(", ")));
  };

  // ----- API: Codes
  const fetchCodes = async (page: number = 1, search: string = "") => {
    setIsFetchingCodes(true);
    try {
      const skip = (page - 1) * 10;
      const params = new URLSearchParams();
      if (projectId) params.append("project_id", projectId);
      params.append("skip", String(skip));
      params.append("limit", "10");
      if (search.trim()) params.append("search", search.trim());

      const res = await get<any>(`/codes?${params.toString()}`);

      const items = res?.data?.items || [];
      setCodes(items);
      setCodeTotal(res?.data?.total || 0);

      const flat: any[] = [];
      const walk = (node: any) => {
        flat.push(node);
        (node.children || []).forEach(walk);
      };
      items.forEach(walk);

      const map: Record<string, string> = {};
      flat.forEach((c) => {
        if (c.id && c.hash_color) map[c.id] = c.hash_color;
      });
      setCodeColorMap(map);
    } catch {
      message.error("Failed to fetch codes.");
    } finally {
      setIsFetchingCodes(false);
    }
  };

  // 🔁 si te Media: rifresko kur ndryshon projectId, page ose search
  useEffect(() => {
    if (!projectId) return;
    fetchCodes(codePage, codeSearch);
  }, [projectId, codePage, codeSearch]);

  const handleAssignCodeClick = async () => {
    setInlineBusy(true);
    if (activeHighlightIdRef.current) {
      await fetchHighlightById(activeHighlightIdRef.current);
      await fetchCodes(1);
      setInlineBusy(false);
      setShowCodePanel(true);
      setPopupPos(null);
      return;
    }
    showTemporaryHighlight();
    await fetchCodes(1);
    setInlineBusy(false);
    setShowCodePanel(true);
    setPopupPos(null);
  };

  const startEditHighlight = async (
    highlightId?: string,
    currentCodes?: { id: string; name: string }[],
    text?: string
  ) => {
    if (!highlightId) return;
    editingHighlightIdRef.current = highlightId;
    editingHighlightTextRef.current = text ?? null;
    setEditingHighlightId(highlightId);
    setInlineBusy(true);
    setUpdatingHighlightId(highlightId);
    try {
      await fetchCodes(1);
      setSelectedCodes(new Set((currentCodes || []).map((c) => c.id)));
      setShowCodePanel(true);
    } finally {
      setInlineBusy(false);
      setUpdatingHighlightId(null);
    }
  };

  // ----- CREATE / UPDATE
  const handleAssignSelectedCodes = async () => {
    // UPDATE
    if (editingHighlightIdRef.current) {
      const codeIds = Array.from(selectedCodes);
      if (!codeIds.length)
        return message.error("Please select at least one code.");
      setIsAssigning(true);
      try {
        // thjesht ndërron kodet, nuk prek content/start/end
        const body: any = { code_ids: codeIds };

        const highlightId = editingHighlightIdRef.current!;
        const res = await put<any>(`highlights/${highlightId}`, body);
        if (res?.type === "error") {
          message.warning("Updating highlight failed.");
        } else {
          message.success("Highlight updated!");
          onHighlightChanged?.();

          const all = codes.flatMap((c) => [c, ...(c.children || [])]);
          const names = codeIds
            .map((id) => all.find((x) => x.id === id)?.name)
            .filter(Boolean) as string[];

          refreshHighlightTooltip(highlightId, names);

          setSelectedHighlights((prev) =>
            prev.map((h) =>
              h.highlightId === highlightId
                ? {
                    ...h,
                    codes: codeIds.map((id, i) => ({
                      id,
                      name: names[i] || "",
                    })),
                  }
                : h
            )
          );

          await fetchHighlightById(highlightId);
        }
      } catch (e) {
        console.error(e);
        message.error("Unexpected error updating highlight.");
      } finally {
        setIsAssigning(false);
        setShowCodePanel(false);
        setSelectedCodes(new Set());
        setEditingHighlightId(null);
        editingHighlightIdRef.current = null;
        editingHighlightTextRef.current = null;
      }
      return;
    }

    // CREATE
    if (!selectedText || !selectionRange || selectedCodes.size === 0) {
      return message.error("Please select text and at least one code.");
    }
    if (!documentIdRef.current) return message.error("Missing document ID.");
    if (!frameDoc) return message.error("Document frame is not ready.");

    const allCodesFlat = codes.flatMap((c) => [c, ...(c.children || [])]);
    const codeIds = Array.from(selectedCodes);
    const selectedCodeObjects = codeIds
      .map((id) => allCodesFlat.find((c) => c.id === id))
      .filter(Boolean) as any[];

    highlightSelection(
      selectedCodeObjects[0]?.hash_color || "#FFD700",
      selectedCodeObjects.map((c) => c.name)
    );

    // pozitat direkt prej Range, me të njëjtën logjikë si buildRangeFromOffsets
    let start = -1;
    let end = -1;
    try {
      const off = getOffsetsFromRange(frameDoc.body, selectionRange);
      start = off.start;
      end = off.end;
    } catch (e) {
      console.error("Failed to compute offsets from range", e);
    }

    const userData = localStorage.getItem("user");
    const userId = userData ? JSON.parse(userData)?.user_id : null;
    if (!userId) return message.error("Missing user ID.");

    setIsAssigning(true);
    try {
      const payload: any = {
        document_id: documentIdRef.current,
        content: getExactRangeText(selectionRange),
        user_id: userId,
        code_id: codeIds,
      };

      if (start !== -1 && end > start) {
        payload.start_position = start;
        payload.end_position = end;
      }

      const res = await post<any>("highlights", payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res?.type === "error") {
        message.warning("Highlight assignment failed.");
      } else {
        message.success("Highlight assigned successfully!");
        onHighlightChanged?.();
        const createdId =
          res?.data?.id ??
          res?.data?.highlight_id ??
          (Array.isArray(res?.data) ? res.data[0]?.id : undefined);

        if (frameDoc && createdId) {
          frameDoc
            .querySelectorAll(`.hs-overlay[data-highlight-id="__pending__"]`)
            .forEach(
              (n) =>
                ((n as HTMLElement).dataset.highlightId = String(createdId))
            );
          refreshHighlightTooltip(
            String(createdId),
            selectedCodeObjects.map((c: any) => c.name)
          );
          await fetchHighlightById(String(createdId));
          setShowCodePanel(true);
        } else {
          await fetchHighlights();
        }
      }
    } catch (e) {
      console.error(e);
      message.error("Unexpected error assigning highlights.");
    } finally {
      setSelectedText("");
      setSelectionRange(null);
      setSelectedCodes(new Set());
      setShowCodePanel(false);
      setIsAssigning(false);
      activeHighlightIdRef.current = null;
      overlayGroupRef.current = null;
    }
  };

  // ----- Delete
  const removeHighlight = (index: number) => {
    const updated = [...selectedHighlights];
    updated.splice(index, 1);
    setSelectedHighlights(updated);
  };
  const deleteHighlight = async (highlightId?: string, index?: number) => {
    if (!highlightId) {
      if (typeof index === "number") removeHighlight(index);
      return;
    }
    try {
      setDeletingId(highlightId);
      const res = await remove<any>(`/highlights/${highlightId}`, {});
      if (res?.type === "success") {
        message.success("Highlight deleted.");
        onHighlightChanged?.();
        if (frameDoc) {
          frameDoc
            .querySelectorAll(`.hs-overlay[data-highlight-id="${highlightId}"]`)
            .forEach((n) => n.remove());
          frameDoc
            .querySelectorAll(
              `.highlighted-code[data-highlight-id="${highlightId}"]`
            )
            .forEach((el) => {
              const node = el as HTMLElement;
              const text = node.textContent || "";
              node.replaceWith(frameDoc.createTextNode(text));
            });
        }
        if (typeof index === "number")
          setSelectedHighlights((prev) => prev.filter((_, i) => i !== index));
        if (editingHighlightIdRef.current === highlightId) {
          setEditingHighlightId(null);
          editingHighlightIdRef.current = null;
          editingHighlightTextRef.current = null;
          setShowCodePanel(false);
          setSelectedCodes(new Set());
        }
      } else {
        message.error("Failed to delete highlight.");
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to delete highlight.");
    } finally {
      setDeletingId(null);
    }
  };

  // ----- Codes tree
  const toggleNode = (id: string) => {
    setExpandedNodes((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handleCheckboxChange = (codeId: string) => {
    setSelectedCodes((prev) => {
      const s = new Set(prev);
      s.has(codeId) ? s.delete(codeId) : s.add(codeId);
      return s;
    });
  };

  // 🔴 NDRYSHIMI I VETËM: gjithë rreshti është klikueshëm
  const renderCodeTree = (nodes: any[]) =>
    nodes.map((node) => {
      const isExpanded = expandedNodes.has(node.id);
      const hasChildren = node.children && node.children.length > 0;
      return (
        <div key={node.id} className="code-tree-node">
          <div className="code-item-with-toggle">
            <div
              className="code-item"
              onClick={() => handleCheckboxChange(node.id)}
            >
              <input
                type="checkbox"
                checked={selectedCodes.has(node.id)}
                onChange={() => handleCheckboxChange(node.id)}
                onClick={(e) => e.stopPropagation()} // mos togglo dy herë kur klikohet vet checkbox
                style={{ marginRight: 8 }}
              />
              <div
                className="color-label"
                style={{ backgroundColor: node.hash_color || "#aaa" }}
              />
              <span>{node.name}</span>
            </div>
            {hasChildren && (
              <div className="toggle-icon" onClick={() => toggleNode(node.id)}>
                {isExpanded ? <DownOutlined /> : <RightOutlined />}
              </div>
            )}
          </div>
          {isExpanded && hasChildren && (
            <div className="subcode-list scroll-x">
              {renderCodeTree(node.children)}
            </div>
          )}
        </div>
      );
    });

  function buildRangeFromOffsets(
    root: Node,
    start: number,
    end: number
  ): Range | null {
    const doc = root.ownerDocument || (root as any as Document);
    const range = doc.createRange();
    let current = 0;
    let startSet = false;

    function walk(node: Node): boolean {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        const next = current + text.length;

        if (!startSet && start >= current && start <= next) {
          range.setStart(node, start - current);
          startSet = true;
        }
        if (startSet && end >= current && end <= next) {
          range.setEnd(node, end - current);
          return true;
        }

        current = next;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (walk(node.childNodes[i])) return true;
        }
      }
      return false;
    }

    walk(root);

    if (!startSet || range.collapsed) return null;
    return range;
  }

  // ❗️Përdor Range API-n vetë për të matur offset-et: setEnd() e trajton
  // saktë boundary-n edhe kur startContainer/endContainer është ELEMENT_NODE
  // (child index si offset), rast që ndodh shpesh kur selektimi mbaron pikërisht
  // në kufi midis dy <span>-ave te text-layer i PDF-it. Walk-i i mëparshëm me
  // krahasim referencash (node === range.startContainer) vetëm brenda degës
  // TEXT_NODE nuk e kapte fare këtë rast dhe kthente -1 (=> "Field required"
  // për start_position/end_position te backend).
  function getOffsetsFromRange(
    root: Node,
    range: Range
  ): { start: number; end: number } {
    const doc = root.ownerDocument || (root as unknown as Document);

    const toBoundaryLength = (container: Node, offset: number): number => {
      const r = doc.createRange();
      r.selectNodeContents(root);
      r.setEnd(container, offset);
      return r.toString().length;
    };

    try {
      const start = toBoundaryLength(range.startContainer, range.startOffset);
      const end = toBoundaryLength(range.endContainer, range.endOffset);
      return { start, end };
    } catch (e) {
      console.error("getOffsetsFromRange failed", e);
      return { start: -1, end: -1 };
    }
  }

  // type BackendHighlight = {
  //   id: string;
  //   content: string;
  //   start_position: number;
  //   end_position: number;
  //   codes: { code_id: string; code_name: string }[];
  // };

  // function renderBackendHighlight(item: BackendHighlight) {
  //   if (!frameDoc) return;

  //   const hay = getVisibleHay();

  //   // 1️⃣ – POZITAT NGA BACKEND (source of truth)
  //   let start = -1;
  //   let end = -1;

  //   if (
  //     typeof item.start_position === "number" &&
  //     typeof item.end_position === "number" &&
  //     item.end_position > item.start_position
  //   ) {
  //     start = item.start_position;
  //     end = item.end_position;
  //   } else if (item.content) {
  //     // fallback – vec nese s’ka pozita në DB (highlights shumë të vjetra)
  //     ({ start, end } = getTextPositions(hay, item.content || ""));
  //   }

  //   if (start === -1 || start === end) {
  //     // s’kem çka vizatojmë
  //     return;
  //   }

  //   const range = buildRangeFromOffsets(frameDoc.body, start, end);
  //   if (!range) return;

  //   const codesList = item.codes || [];
  //   const firstCodeId = codesList[0]?.code_id;
  //   const codeNames = codesList.map((c) => c.code_name);
  //   const color = codeColorMap[firstCodeId] || "#A072EB";

  //   if (isPdfDoc) {
  //     // 🧾 PDF – përdor textLayer overlay
  //     const rects = getPdfSelectionRectsByTextLayer(frameDoc, range);
  //     rects.forEach(({ rect, textLayer, left, top }) => {
  //       const container = ensureTextLayerOverlay(textLayer);
  //       const box = frameDoc.createElement("div");
  //       box.className = "hs-overlay";
  //       box.style.left = `${left}px`;
  //       box.style.top = `${top}px`;
  //       box.style.width = `${rect.width}px`;
  //       box.style.height = `${rect.height}px`;
  //       box.style.background = `${color}66`;
  //       box.dataset.highlightId = String(item.id);
  //       box.title = codeNames.join(", ");
  //       (box.style as any).pointerEvents = "auto";
  //       container.appendChild(box);
  //     });
  //   } else {
  //     // 🌐 HTML – vizato overlay sipër body
  //     const rects = Array.from(range.getClientRects()).filter(
  //       (r) => r.width > 0.5 && r.height > 0.5
  //     );
  //     const bodyRect = frameDoc.body.getBoundingClientRect();

  //     rects.forEach((r) => {
  //       const box = frameDoc.createElement("div");
  //       box.className = "hs-overlay";
  //       box.style.left = `${r.left - bodyRect.left}px`;
  //       box.style.top = `${r.top - bodyRect.top}px`;
  //       box.style.width = `${r.width}px`;
  //       box.style.height = `${r.height}px`;
  //       box.style.background = `${color}66`;
  //       box.dataset.highlightId = String(item.id);
  //       box.title = codeNames.join(", ");
  //       (box.style as any).pointerEvents = "auto";
  //       frameDoc.body.appendChild(box);
  //     });
  //   }
  // }

  type BackendHighlight = {
    id: string;
    content: string;
    start_position: number;
    end_position: number;
    codes: { code_id: string; code_name: string }[];
  };

  function renderBackendHighlight(item: BackendHighlight) {
    if (!frameDoc) return;

    const hay = getVisibleHay();

    // 1️⃣ – tentojmë me koordinatat nga backend
    let start = -1;
    let end = -1;

    if (
      typeof item.start_position === "number" &&
      typeof item.end_position === "number" &&
      item.end_position > item.start_position
    ) {
      start = item.start_position;
      end = item.end_position;
    }

    // 2️⃣ – nëse koordinatat s’janë të vlefshme, përdor content-in nga DB
    if (start === -1 || end <= start) {
      const pos = getTextPositions(hay, item.content || "");
      start = pos.start;
      end = pos.end;
    }

    // 3️⃣ – nëse prap nuk kemi pozita të vlefshme, s’ka highlight
    if (start === -1 || end <= start) {
      return;
    }

    // 4️⃣ – ndërto Range nga offsetet
    let range = buildRangeFromOffsets(frameDoc.body, start, end);

    // 5️⃣ – fallback final: nëse range del null (shpesh DOCX),
    // provo prapë me llogarit pozitat prej tekstit
    if (!range) {
      const pos = getTextPositions(hay, item.content || "");
      if (pos.start !== -1 && pos.end > pos.start) {
        range = buildRangeFromOffsets(frameDoc.body, pos.start, pos.end);
      }
    }

    if (!range) return;

    const codesList = item.codes || [];
    const firstCodeId = codesList[0]?.code_id;
    const codeNames = codesList.map((c) => c.code_name);
    const color = codeColorMap[firstCodeId] || "#A072EB";

    if (isPdfDoc) {
      // 🧾 PDF – overlay brenda textLayer
      const rects = getPdfSelectionRectsByTextLayer(frameDoc, range);
      rects.forEach(({ rect, textLayer, left, top }) => {
        const container = ensureTextLayerOverlay(textLayer);
        const box = frameDoc.createElement("div");
        box.className = "hs-overlay";
        box.style.left = `${left}px`;
        box.style.top = `${top}px`;
        box.style.width = `${rect.width}px`;
        box.style.height = `${rect.height}px`;
        box.style.background = `${color}66`;
        box.dataset.highlightId = String(item.id);
        box.title = codeNames.join(", ");
        (box.style as any).pointerEvents = "auto";
        container.appendChild(box);
      });
    } else {
      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0.5 && r.height > 0.5
      );
      const bodyRect = frameDoc.body.getBoundingClientRect();

      rects.forEach((r) => {
        const box = frameDoc.createElement("div");
        box.className = "hs-overlay";
        box.style.left = `${r.left - bodyRect.left}px`;
        box.style.top = `${r.top - bodyRect.top}px`;
        box.style.width = `${r.width}px`;
        box.style.height = `${r.height}px`;
        box.style.background = `${color}66`;
        box.dataset.highlightId = String(item.id);
        box.title = codeNames.join(", ");
        (box.style as any).pointerEvents = "auto";
        frameDoc.body.appendChild(box);
      });
    }
  }

  // ----- Fetch highlight(s)
  const fetchHighlightById = async (highlightId: string) => {
    setIsFetchingHighlight(true);
    try {
      const res = await get<any>(`/highlights/${highlightId}`);
      const item = res?.data;
      const codesList = Array.isArray(item?.codes)
        ? item.codes.map((c: any) => ({ id: c.code_id, name: c.code_name }))
        : [];
      const oneCard: HighlightedData = {
        text: item?.content ?? "",
        code: { id: codesList[0]?.id || "", name: codesList[0]?.name || "" },
        fileName: item?.document_name || "Unknown",
        codes: codesList,
        highlightId: item?.id,
      };
      refreshHighlightTooltip(
        String(item?.id),
        codesList.map((c: any) => c.name)
      );
      setSelectedHighlights([oneCard]);
    } catch (e) {
      console.error(e);
      message.error("Failed to load highlight.");
    } finally {
      setIsFetchingHighlight(false);
    }
  };

  const fetchHighlights = async () => {
    if (!projectId || !documentIdRef.current) return;
    try {
      const res = await get<any>(
        `/highlights?project_id=${projectId}&document_id=${documentIdRef.current}`
      );
      const payload = res?.data;
      const arr = Array.isArray(payload) ? payload : payload?.items ?? [];

      const grouped: HighlightedData[] = arr.map((item: any) => {
        const codesList =
          (item.codes ?? []).map((c: any) => ({
            id: c.code_id,
            name: c.code_name,
          })) || [];
        return {
          text: item.content,
          code: { id: codesList[0]?.id || "", name: codesList[0]?.name || "" },
          fileName: item.document_name || "Unknown",
          codes: codesList,
          highlightId: item.id,
        };
      });

      setSelectedHighlights(grouped);

      if (frameDoc) {
        arr.forEach((item: any) =>
          renderBackendHighlight({
            id: item.id,
            content: item.content,
            start_position: item.start_position,
            end_position: item.end_position,
            codes: item.codes || [],
          })
        );
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to load highlights.");
    }
  };

  useEffect(() => {
    if (!frameDoc || !projectId || !documentIdRef.current) return;
    if (!codeColorMap || Object.keys(codeColorMap).length === 0) return;
    fetchHighlights();
  }, [frameDoc, projectId, documentId, codeColorMap]);

  const submitButtonLabel = editingHighlightId
    ? isAssigning
      ? "Updating..."
      : "Update Highlight"
    : isAssigning
    ? "Assigning..."
    : "Assign Selected Codes";
  const anyGlobalBusy = isAssigning || isFetchingCodes || isFetchingHighlight;

  return (
    <div className="text-editor-layout">
      <div className="text-editor-left">
        <button className="back-button" onClick={onBack}>
          <ArrowLeftOutlined />
          Back
        </button>
        <h3 className="editable-document-title">Editable Document</h3>
        <div className="document-wrapper" style={{ width: "100%" }}>
          <Spin
            spinning={anyGlobalBusy}
            tip="Working..."
            style={{ minWidth: 530 }}
          >
            <iframe
              ref={iframeRef}
              sandbox="allow-same-origin allow-scripts"
              style={{
                width: "100%",
                minWidth: 530,
                padding: "1rem",
                minHeight: 600,
                border: "1px solid #F4D9FF",
                background: "#fff",
              }}
              title="doc-view"
            />
          </Spin>
        </div>

        {popupPos && !inlineBusy && (
          <div
            className="assign-code-popup-card"
            style={{ position: "fixed", top: popupPos.y, left: popupPos.x }}
            onClick={handleAssignCodeClick}
          >
            <div className="assign-icon">+</div>
            <span className="assign-label">Assign code</span>
          </div>
        )}
        {popupPos && inlineBusy && (
          <div
            style={{
              position: "fixed",
              top: popupPos.y,
              left: popupPos.x,
              transform: "translate(-50%, -50%)",
              zIndex: 1000,
              background: "#fff",
              border: "1px solid #eee",
              borderRadius: 10,
              padding: "8px 10px",
              boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              pointerEvents: "none",
            }}
          >
            <LoadingOutlined spin />
            <span style={{ fontSize: 12, color: "#555" }}>Loading…</span>
          </div>
        )}
      </div>

      {(showCodePanel || selectedHighlights.length > 0) && (
        <div className="text-editor-right">
          {selectedHighlights.length > 0 && (
            <div className="selection-info">
              <h3>Selection info</h3>
              {selectedHighlights.map((item, index) => (
                <div className="highlight-card" key={index}>
                  <button
                    className="remove-btn"
                    disabled={!!deletingId}
                    onClick={() => deleteHighlight(item.highlightId, index)}
                    title="Delete highlight"
                  >
                    ✕
                  </button>
                  <div className="doc-name">📎 {item.fileName}</div>
                  <div className="code-name">
                    📌{" "}
                    {item.codes && item.codes.length
                      ? item.codes.map((c) => c.name).join(", ")
                      : item.code.name}
                  </div>
                  {!!item.highlightId && (
                    <button
                      className="assign-selected-btn"
                      style={{ marginTop: 10 }}
                      onClick={() =>
                        startEditHighlight(
                          item.highlightId,
                          item.codes,
                          item.text
                        )
                      }
                      disabled={
                        !!deletingId || updatingHighlightId === item.highlightId
                      }
                    >
                      {updatingHighlightId === item.highlightId ? (
                        <>
                          <LoadingOutlined style={{ marginRight: 6 }} spin />
                          Loading...
                        </>
                      ) : (
                        "Update highlight"
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {showCodePanel && (
            <>
              <h3>Codes</h3>
              <Input.Search
                placeholder="Search by code name"
                value={codeSearch}
                allowClear
                style={{ marginBottom: 20 }}
                onChange={(e) => {
                  const val = e.target.value;
                  // si te Media: reset page në 1 dhe veç update state;
                  // useEffect do thërret fetchCodes automatikisht
                  setCodePage(1);
                  setCodeSearch(val);
                }}
              />
              <Spin spinning={isFetchingCodes || isFetchingHighlight}>
                {!codes.length ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Spin />
                  </div>
                ) : (
                  <>
                    <div className="code-list">{renderCodeTree(codes)}</div>
                    <div style={{ marginTop: 16, textAlign: "right" }}>
                      <Pagination
                        current={codePage}
                        pageSize={10}
                        total={codeTotal}
                        onChange={(p) => {
                          setCodePage(p);
                          // fetchCodes(p, codeSearch) thirret nga useEffect
                        }}
                        size="small"
                      />
                    </div>
                  </>
                )}
              </Spin>
              <button
                onClick={handleAssignSelectedCodes}
                className="assign-selected-btn"
                style={{ marginTop: 16 }}
              >
                {submitButtonLabel}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TextSelector;
